import { FormEvent, useContext, useEffect, useState, useRef } from 'react';
import {
	FieldPath,
	FieldValues,
	get,
	useController,
	useFormContext,
} from 'react-hook-form';

import CurrencyInput, { CurrencyInputProps } from './CurrencyInput';
import { formatCurrency, parseCurrency } from 'src/lib/utils';
import { CampaignContext } from 'src/contexts';
import useBudgetManager from 'src/hooks/useBudgetManager';
import { IChannelGroup } from 'src/lib/schemas';
import { ICampaignBudget } from 'src/lib/schemas/campaign/newFlowCampaign';

export interface CurrencyInputHookProps<T extends FieldValues>
	extends CurrencyInputProps {
	name: FieldPath<T>;
	requirementsLabel?: string;
	availableChannelGroups: IChannelGroup[] | null;
	setCurrentBudget: (val: ICampaignBudget) => void;
	requirementsLabelStyle?: React.CSSProperties;
	setIsSaving?: React.Dispatch<React.SetStateAction<boolean>>;
}

function CurrencyInputHook<T extends FieldValues = FieldValues>({
	name,
	requirementsLabel,
	availableChannelGroups,
	requirementsLabelStyle,
	setCurrentBudget,
	setIsSaving,
	...props
}: CurrencyInputHookProps<T>) {
	const {
		register,
		control,
		formState: { errors },
	} = useFormContext();
	const { campaign } = useContext(CampaignContext);
	const { queueBudgetUpdate } = useBudgetManager();

	const { field } = useController({ control, name });
	const { onChange } = field;

	const [currentValue, setCurrentValue] = useState(() => {
		return campaign?.budget && typeof campaign.budget.total === 'number'
			? formatCurrency(campaign.budget.total)
			: '';
	});

	const [isUserInput, setIsUserInput] = useState(false);
	const userInputTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const formatTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (campaign?.budget && typeof campaign.budget.total === 'number') {
			const formattedValue = formatCurrency(campaign.budget.total);

			// Only skip update if user is actively typing in this input
			if (!isUserInput && currentValue !== formattedValue) {
				setCurrentValue(formattedValue);
			}
		}
	}, [campaign?.budget?.total]);

	// Budget updates are now handled by the queue system in useBudgetManager
	// The handleImmediateUpdate function queues updates and provides optimistic UI

	// Function to handle immediate formatting and breakdown update
	const handleImmediateUpdate = (value: string) => {
		if (!campaign || !availableChannelGroups) return;

		const newTotal = parseFloat(parseCurrency(value)) || 0;
		if (isNaN(newTotal) || newTotal === (campaign.budget?.total ?? 0)) return;

		// IMMEDIATE UI UPDATE: Format input and update breakdown right away
		const formattedValue = formatCurrency(newTotal);

		// Queue budget update and get optimistic budget immediately
		const optimisticBudget = queueBudgetUpdate({
			total: newTotal,
			placements: campaign.placements,
			availableChannelGroups: availableChannelGroups || undefined,
			source: 'CurrencyInputHook',
		});

		// Update both input display and budget breakdown immediately
		setCurrentValue(formattedValue);
		if (optimisticBudget) {
			setCurrentBudget(optimisticBudget);
		}
	};

	// Cleanup timeouts on unmount
	useEffect(() => {
		return () => {
			if (userInputTimeoutRef.current) {
				clearTimeout(userInputTimeoutRef.current);
			}
			if (formatTimeoutRef.current) {
				clearTimeout(formatTimeoutRef.current);
			}
		};
	}, []);

	const handleChange = (event: FormEvent<HTMLInputElement>) => {
		const inputValue = event.currentTarget.value;

		setIsSaving && setIsSaving(true);

		// Handle empty values by converting them to '0'
		const valueToSet =
			inputValue === '' || inputValue === '$' ? '0' : inputValue;

		setCurrentValue(valueToSet);
		setIsUserInput(true);

		// Clear any existing timeouts
		if (userInputTimeoutRef.current) {
			clearTimeout(userInputTimeoutRef.current);
		}
		if (formatTimeoutRef.current) {
			clearTimeout(formatTimeoutRef.current);
		}

		// Debounce the API call - wait for user to finish typing (800ms)
		// This prevents multiple requests when typing "100" (1 -> 10 -> 100)
		formatTimeoutRef.current = setTimeout(() => {
			handleImmediateUpdate(valueToSet);
		}, 800);

		// Reset isUserInput after 2 seconds of inactivity
		userInputTimeoutRef.current = setTimeout(() => {
			setIsUserInput(false);
		}, 2000);

		onChange(parseCurrency(valueToSet));
	};

	const error = get(errors, name);

	return (
		<CurrencyInput
			id={name}
			error={error?.message}
			requirementsLabelStyle={requirementsLabelStyle}
			requirementsLabel={requirementsLabel}
			{...props}
			inputProps={{
				...props.inputProps,
				onChange: handleChange,
				value: currentValue,
			}}
			ref={register(name).ref}
		/>
	);
}

export default CurrencyInputHook;
